import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/react";
import { makeApi } from "@/lib/api";
import {
  LayoutDashboard,
  BookOpen,
  FileText,
  ListChecks,
  Grid3x3,
  Sparkles,
  Calendar,
  Users,
  CreditCard,
  Plug,
  ScrollText,
  Activity,
  Shield,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, id: "admin-nav-overview" },
  { to: "/admin/subjects", label: "Exam Families & Subjects", icon: BookOpen, id: "admin-nav-subjects" },
  { to: "/admin/syllabus", label: "Syllabus & Units", icon: FileText, id: "admin-nav-syllabus" },
  { to: "/admin/question-bank", label: "Question Bank", icon: ListChecks, id: "admin-nav-question-bank" },
  { to: "/admin/blueprints", label: "Blueprints", icon: Grid3x3, id: "admin-nav-blueprints" },
  { to: "/admin/ai-centre", label: "AI Centre", icon: Sparkles, id: "admin-nav-ai-centre" },
  { to: "/admin/exam-dates", label: "Exam Dates", icon: Calendar, id: "admin-nav-exam-dates" },
  { to: "/admin/users", label: "Users & Roles", icon: Users, id: "admin-nav-users" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, id: "admin-nav-payments" },
  { to: "/admin/integrations", label: "Integration Settings", icon: Plug, id: "admin-nav-integrations" },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, id: "admin-nav-audit-logs" },
  { to: "/admin/health", label: "App Health", icon: Activity, id: "admin-nav-health" },
];

const REVIEWER_NAV = ["admin-nav-question-bank", "admin-nav-blueprints"];

export default function AdminLayout() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const api = makeApi(getToken);
    api
      .get("/me")
      .then((r) => {
        setMe(r.data);
        if (!["admin", "reviewer"].includes(r.data.role)) navigate("/app", { replace: true });
      })
      .catch(() => navigate("/app", { replace: true }));
  }, [isLoaded, isSignedIn, getToken, navigate]);

  if (me && !["admin", "reviewer"].includes(me.role)) return null;
  const nav = me?.role === "reviewer" ? NAV.filter((n) => REVIEWER_NAV.includes(n.id)) : NAV;

  return (
    <div className="min-h-screen flex bg-[#FAFAFC]">
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-white text-slate-700 border-r border-slate-200 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-600 text-white grid place-items-center font-bold font-display">J</div>
            <span className="font-display font-bold text-base text-slate-900">Admin</span>
          </Link>
          <button className="lg:hidden text-slate-500" onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="p-3 space-y-0.5 max-h-[calc(100vh-4rem)] overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          <NavLink
            to="/app"
            onClick={() => setOpen(false)}
            data-testid="admin-nav-back-student"
            className="mt-4 flex items-center gap-3 px-3 py-2 rounded-md text-sm bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            <Shield className="h-4 w-4" /> Back to student app
          </NavLink>
        </nav>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="text-sm text-slate-500">
              {me ? `Admin · ${me.email}` : ""}
            </div>
          </div>
          <UserButton />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>

      {open && (
        <button className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setOpen(false)} aria-label="Close overlay" />
      )}
    </div>
  );
}
